import React, { useEffect, useMemo, useState } from "react";
import { Editdoc, Viewdoc, Deleteicon } from "../../Icon";
import { useNavigate } from "react-router-dom";
import { FaPrint } from "react-icons/fa";
import axios from "axios";
import { API_BASE_URL } from "../../utils/CONSTANTS";
import Header from "../../components/header";
import { canEditFaiPdi } from "../../utils/roles";

const Pdimenu = () => {
    const navigate = useNavigate();
    const [complaints, setComplaints] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedComplaint, setSelectedComplaint] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const canEdit = canEditFaiPdi();

    const getfdidata = async () => {
        try {
            const mode = "pdi";
            const response = await axios.get(`${API_BASE_URL}/api/product/getfdi/${mode}`);
            const result = response.data;
            if (Array.isArray(result.isdata)) {
                const allFdi = result.isdata.map(item => {
                    // normalize fields defensively
                    const id = item._id || item.id || `${Math.random()}`; // fallback id
                    const customerName = (item.partName ?? item.customerName ?? "") + "";
                    const reportno = (item.reportno ?? item.productName ?? "") + "";
                    const cardno = (item.cardno ?? "") + "";
                    const drgno = (item.drgno ?? "") + "";
                    const pdino = (item.pdino ?? "") + "";

                    return {
                        id,
                        customerName,
                        reportno,
                        cardno,
                        drgno,
                        pdino
                    };
                });
                setComplaints(allFdi);
            } else {
                setComplaints([]);
            }
        } catch (err) {
            console.log("Error fetching FDI data:", err?.message ?? err);
            setComplaints([]);
        }
    };

    useEffect(() => {
        getfdidata();
    }, []);

    const handleDelete = async () => {
        if (!selectedComplaint) return;
        try {
            // prefer deleting by id if we have one
            const idToDelete = selectedComplaint.id ?? selectedComplaint;
            await axios.delete(`${API_BASE_URL}/api/product/deleteproduct/${idToDelete}`);

            setComplaints(prev => prev.filter(item => item.id !== idToDelete));
            setShowModal(false);
            setSelectedComplaint(null);
        } catch (err) {
            console.log("Error deleting:", err?.message ?? err);
        }
    };

    // Derived filtered list based on searchTerm (defensive), sorted by Report No.
    const filteredComplaints = useMemo(() => {
        const filtered = complaints.filter(item => {
        if (!searchTerm || searchTerm.trim() === "") return true;
        const q = searchTerm.trim().toLowerCase();

        // coerce to string first, then lower-case
        const cardnoStr = (item.cardno !== undefined && item.cardno !== null) ? String(item.cardno).toLowerCase() : "";
        const custName = (item.customerName !== undefined && item.customerName !== null) ? String(item.customerName).toLowerCase() : "";
        const reportNo = (item.reportno !== undefined && item.reportno !== null) ? String(item.reportno).toLowerCase() : "";

        return cardnoStr.includes(q) || custName.includes(q) || reportNo.includes(q);
        });

        return [...filtered].sort((a, b) => {
            const aNum = Number(a.reportno);
            const bNum = Number(b.reportno);
            if (!isNaN(aNum) && !isNaN(bNum)) return bNum - aNum;
            return String(b.reportno ?? "").localeCompare(String(a.reportno ?? ""), undefined, { numeric: true });
        });
    }, [complaints, searchTerm]);

    return (
        <>
            <Header />

            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="max-w-6xl mx-auto bg-white shadow-md p-6 rounded-lg">
                    <h1 className="text-2xl font-semibold mb-4">PDI Menu</h1>

                    <div className="mb-4 flex gap-2">
                        <input
                            type="text"
                            placeholder="Search by Card No, Consumer Name or Report No"
                            className="flex-1 p-2 border border-gray-300 rounded-md"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button
                            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                            onClick={() => { setSearchTerm(""); }}
                        >
                            Clear
                        </button>
                    </div>

                    <table className="w-full border-collapse border border-gray-300">
                        <thead>
                            <tr className="bg-gray-200">
                                <th className="border border-gray-300 px-4 py-2">PDI No.</th>
                                <th className="border border-gray-300 px-4 py-2">Report No.</th>
                                <th className="border border-gray-300 px-4 py-2">Card No.</th>
                                <th className="border border-gray-300 px-4 py-2">Consumer Name</th>
                                <th className="border border-gray-300 px-4 py-2">Drg No.</th>
                                <th className="border border-gray-300 px-4 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredComplaints.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center p-4 text-gray-500">No records found.</td>
                                </tr>
                            ) : (
                                filteredComplaints.map((complaint) => (
                                    <tr key={complaint.id} className="text-center">
                                        <td className="border border-gray-300 px-4 py-2">{complaint.pdino}</td>
                                        <td className="border border-gray-300 px-4 py-2">{complaint.reportno}</td>
                                        <td className="border border-gray-300 px-4 py-2">{complaint.cardno}</td>
                                        <td className="border border-gray-300 px-4 py-2">{complaint.customerName}</td>
                                        <td className="border border-gray-300 px-4 py-2">{complaint.drgno}</td>
                                        <td className="flex justify-around border border-gray-300 px-4 py-2">
                                            <button className="flex flex-col items-center px-3 py-1 rounded-md hover:bg-slate-300" onClick={() => navigate("/fdipdf", { state: { name: complaint.customerName } })}>
                                                <Viewdoc />
                                                <div>View</div>
                                            </button>
                                            {canEdit && (
                                              <>
                                            <button className="flex flex-col items-center px-3 py-1 rounded-md hover:bg-slate-300" onClick={() => navigate("/fdiform", { state: { mode: "update", name: complaint.customerName } })}>
                                                <Editdoc />
                                                <div>Edit</div>
                                            </button>
                                            <button className="flex flex-col items-center px-3 py-1 rounded-md hover:bg-slate-300" onClick={() => { setSelectedComplaint(complaint); setShowModal(true); }}>
                                                <Deleteicon />
                                                <div>Delete</div>
                                            </button>
                                              </>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {showModal && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-md shadow-md">
                            <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
                            <p>Are you sure you want to delete {selectedComplaint?.customerName || selectedComplaint?.id || selectedComplaint}?</p>
                            <div className="flex justify-end mt-4">
                                <button className="bg-gray-300 px-4 py-2 rounded-md mr-2" onClick={() => setShowModal(false)}>Cancel</button>
                                <button
                                    className="bg-red-500 text-white px-4 py-2 rounded-md"
                                    onClick={() => handleDelete()}
                                >
                                    Delete
                                </button>

                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Pdimenu;
